import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { useSEO } from "@/hooks/useSEO";
import {
  ArrowLeft, ShoppingBag, Loader2, X, CreditCard, DollarSign,
  Smartphone, Check, Copy, ArrowRight, Palette, Heart, Truck, MapPin
} from "lucide-react";
import { toast } from "sonner";

/* ═══ DESIGN TOKENS (synced with MarkerCollection) ═══ */
const C = {
  bg:    "#070707",
  bg2:   "#11100E",
  card:  "#181614",
  paper: "#F5F2EB",
  text:  "#F5F2EB",
  text2: "#B5B0A7",
  gold:  "#C9A055",
  goldBr:"#E0BC72",
  red:   "#FF3B30",
  bSub:  "rgba(245,242,235,0.10)",
  bGold: "rgba(201,160,85,0.35)",
};
const F = {
  brush: "'Permanent Marker', cursive",
  cond:  "'Saira Condensed', sans-serif",
  hand:  "'Caveat', cursive",
  sans:  "'DM Sans', 'Outfit', sans-serif",
};

/* Legacy aliases for checkout (unchanged) */
const fBrush = { fontFamily: F.brush };
const fScript = { fontFamily: F.hand };
const fSans  = { fontFamily: F.sans };
const fCond  = { fontFamily: F.cond };
const GOLD  = C.gold;
const BLACK = C.bg;
const CREAM = C.text;
const GRAY  = C.text2;
const DGRAY = C.bg2;

/* ═══ VISIBILITY HOOK ═══ */
function useVisible(threshold = 0.15) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, v];
}

/* ═══ MARKER STROKE ═══ */
const MarkerStroke = ({ color = C.gold, width = "100%", height = "6px", className = "" }) => (
  <div className={className} style={{
    width, height, background: color, borderRadius: "3px",
    transform: "rotate(-0.8deg)", opacity: 0.85,
    boxShadow: `0 0 12px ${color}40`,
  }} />
);

/* ═══ INK SPLAT ═══ */
const InkSplat = ({ className = "", style = {} }) => (
  <svg viewBox="0 0 200 200" className={className} style={style} xmlns="http://www.w3.org/2000/svg">
    <circle cx="100" cy="100" r="60" fill="currentColor" opacity="0.7"/>
    <circle cx="140" cy="70" r="20" fill="currentColor" opacity="0.5"/>
    <circle cx="60" cy="130" r="25" fill="currentColor" opacity="0.6"/>
    <circle cx="155" cy="130" r="12" fill="currentColor" opacity="0.4"/>
  </svg>
);

export default function MarkerCollectionDetail() {
  useSEO("marker-collection");
  const { slug } = useParams();
  const [art, setArt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [discount, setDiscount] = useState({ enabled: false, per_item: 0, min_qty: 2 });

  useEffect(() => {
    Promise.all([
      http.get(`/marker-collection/public/products/${slug}`),
      http.get("/marker-collection/public/discount-info"),
    ]).then(([artRes, discRes]) => {
      setArt(artRes.data);
      setDiscount(discRes.data);
    }).catch(() => setArt(null))
      .finally(() => setLoading(false));
  }, [slug]);

  // Force visibility after loading completes (refs attach after early returns)
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { if (!loading && art) setLoaded(true); }, [loading, art]);

  // Load charity stats if this artwork has charity enabled
  const [charityStats, setCharityStats] = useState(null);
  useEffect(() => {
    if (!art?.charity?.enabled || !art?.id) return;
    http.get(`/marker-collection/public/charity-stats/${art.id}`)
      .then(r => setCharityStats(r.data))
      .catch(() => {});
  }, [art]);

  if (loading) return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center" style={{ background: BLACK }}>
        <Loader2 size={28} className="animate-spin" style={{ color: GOLD }} />
      </div>
    </Layout>
  );

  if (!art) return (
    <Layout>
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: BLACK }}>
        <p className="text-sm tracking-wider uppercase" style={{ ...fSans, color: GRAY }}>Artwork not found</p>
        <Link to="/marker-collection" className="text-xs tracking-[0.2em] uppercase underline" style={{ ...fSans, color: GOLD }}>Back to Collection</Link>
      </div>
    </Layout>
  );

  const isSold = art.original_status === "Sold";
  const isSoldOut = art.quantity != null && art.quantity <= 0;
  const canBuy = !isSold && !isSoldOut && art.price && art.price > 0;
  const isOriginal = art.art_type === "Original";
  const maxQty = isOriginal ? 1 : (art.quantity != null ? art.quantity : 99);
  const mainImage = art.main_image ? resolveImage(art.main_image) : null;

  return (
    <Layout>
      <div className="min-h-screen" style={{ background: C.bg, color: C.text }} data-testid="mc-detail-page">
        <style>{`
          @keyframes mc-grain { 0%,100% { transform: translate(0,0); } }
          @keyframes mc-shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
          .mc-grit { position: relative; }
          .mc-grit::before { content:''; position:absolute; inset:0; width:100%; height:100%; background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"); pointer-events:none; z-index:0; }
          .mc-shimmer-gold { background: linear-gradient(90deg, ${C.gold}, ${C.goldBr}, ${C.gold}); background-size: 200% auto; animation: mc-shimmer 3s linear infinite; }
        `}</style>

        {/* ─── Back nav ─── */}
        <div className="mc-grit overflow-hidden" style={{ background: C.bg2, borderBottom: `1px solid ${C.bSub}` }}>
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between relative z-10">
            <Link to="/marker-collection" className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase hover:opacity-80 transition-opacity"
              style={{ fontFamily: F.sans, color: C.text2 }} data-testid="mc-detail-back">
              <ArrowLeft size={14} /> Back to Collection
            </Link>
            {art.art_type && (
              <span className="text-[9px] tracking-[0.3em] uppercase font-bold px-3 py-1"
                style={{ fontFamily: F.sans, background: isOriginal ? C.red : `${C.gold}15`, color: isOriginal ? "#fff" : C.gold, border: isOriginal ? "none" : `1px solid ${C.bGold}` }}>
                {art.art_type}
              </span>
            )}
          </div>
        </div>

        {/* Shimmer gold line */}
        <div className="h-[2px] mc-shimmer-gold" />

        {/* ─── Main content ─── */}
        <div className="mc-grit overflow-hidden" style={{ background: C.bg }}>
          <InkSplat className="absolute top-[-30px] right-[-40px] w-48 h-48 opacity-[0.03] pointer-events-none z-0" style={{ color: C.gold }} />
          <div className="max-w-7xl mx-auto px-6 py-12 md:py-16 relative z-10">
            <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16">
              {/* ─── Image column ─── */}
              <div className={`transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                <div className="relative">
                  {mainImage ? (
                    <div className="relative group">
                      {/* Glow behind */}
                      <div className="absolute -inset-6 blur-3xl opacity-15 pointer-events-none"
                        style={{ background: `radial-gradient(circle, ${C.gold} 0%, transparent 70%)` }} />
                      {/* Frame */}
                      <div className="relative border-[5px] shadow-[0_20px_60px_rgba(0,0,0,0.5)]" style={{ borderColor: "#2a2520", background: C.card }}>
                        <img src={mainImage} alt={art.title} className="w-full relative z-10 group-hover:scale-[1.02] transition-transform duration-700"
                          data-testid="mc-detail-image" />
                      </div>
                      {/* Corner accents */}
                      <div className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 z-20" style={{ borderColor: C.gold }} />
                      <div className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 z-20" style={{ borderColor: C.gold }} />
                      <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 z-20" style={{ borderColor: C.gold }} />
                      <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 z-20" style={{ borderColor: C.gold }} />
                    </div>
                  ) : (
                    <div className="aspect-square flex items-center justify-center border-[5px]"
                      style={{ background: C.card, borderColor: "#2a2520" }}>
                      <Palette size={56} style={{ color: `${C.gold}20` }} />
                    </div>
                  )}
                </div>
              </div>

              {/* ─── Details column ─── */}
              <div className={`flex flex-col justify-center transition-all duration-700 delay-200 ${loaded ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
                {/* Type badge */}
                {art.art_type && (
                  <div className="mb-4" data-testid="mc-detail-type">
                    <span className="inline-block text-[10px] tracking-[0.4em] uppercase font-bold px-4 py-1.5"
                      style={{ fontFamily: F.sans, background: isOriginal ? C.red : C.gold, color: isOriginal ? "#fff" : C.bg }}>
                      {art.art_type}{isOriginal ? " — 1 OF 1" : ""}
                    </span>
                  </div>
                )}

                {/* Title */}
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight leading-[0.95] mb-3"
                  style={{ fontFamily: F.cond, color: C.text }} data-testid="mc-detail-title">
                  {art.title}
                </h1>

                <MarkerStroke width="60px" className="mb-5" />

                {/* Description */}
                {art.description && (
                  <p className="text-sm leading-relaxed mb-6 max-w-md"
                    style={{ fontFamily: F.sans, color: C.text2 }} data-testid="mc-detail-desc">
                    {art.description}
                  </p>
                )}

                {/* Meta grid */}
                <div className="space-y-3 mb-8 pt-6" style={{ borderTop: `1px solid ${C.bSub}` }}>
                  {[
                    art.medium && ["Medium", art.medium],
                    art.dimensions && ["Size", art.dimensions],
                    art.year && ["Year", art.year],
                    art.signed && ["Signed", "Yes, by the artist"],
                  ].filter(Boolean).map(([label, val], i) => (
                    <div key={label} className={`flex gap-6 transition-all duration-500 ${loaded ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"}`}
                      style={{ transitionDelay: `${400 + i * 80}ms` }}>
                      <span className="text-[10px] tracking-[0.2em] uppercase w-24 flex-shrink-0"
                        style={{ fontFamily: F.sans, color: `${C.gold}90` }}>{label}</span>
                      <span className="text-sm" style={{ fontFamily: F.sans, color: C.text }}>{val}</span>
                    </div>
                  ))}
                </div>

                {/* ─── Price & Buy ─── */}
                {isSold || isSoldOut ? (
                  <div className="px-6 py-4 inline-block" style={{ border: `2px solid rgba(255,255,255,0.1)` }}>
                    <span className="text-xs tracking-[0.3em] uppercase" style={{ ...fSans, color: GRAY }}>
                      {isSold ? "SOLD" : "SOLD OUT"}
                    </span>
                  </div>
                ) : art.price ? (
                  <div>
                    {art.on_sale && art.sale_price > 0 && art.sale_price < art.price ? (
                      <div className="flex items-baseline gap-3 mb-2">
                        <p className="text-4xl font-black" style={{ ...fCond, color: "#FF3B30" }} data-testid="mc-detail-price">
                          ${Number(art.sale_price).toFixed(2)}
                        </p>
                        <p className="text-xl font-bold line-through" style={{ ...fCond, color: GRAY }} data-testid="mc-detail-original-price">
                          ${Number(art.price).toFixed(2)}
                        </p>
                        <span className="text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-0.5" style={{ background: "#FF3B30", color: "#fff" }}>SALE</span>
                      </div>
                    ) : (
                      <p className="text-4xl font-black mb-2" style={{ ...fCond, color: CREAM }} data-testid="mc-detail-price">
                        ${Number(art.price).toFixed(2)}
                      </p>
                    )}
                    {art.quantity != null && (
                      <p className="text-xs mb-4" style={{ ...fSans, color: GRAY }} data-testid="mc-detail-stock">
                        {art.quantity <= 5 ? `Only ${art.quantity} left` : `${art.quantity} available`}
                      </p>
                    )}

                    {/* Multi-print discount banner */}
                    {discount.enabled && !isOriginal && (
                      <div className="flex items-start gap-3 px-4 py-3 mb-5"
                        style={{ background: `${GOLD}10`, border: `2px solid ${GOLD}30` }}
                        data-testid="mc-discount-banner">
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5"
                          style={{ ...fSans, background: GOLD, color: BLACK }}>%</div>
                        <div>
                          <p className="text-sm font-bold" style={{ ...fSans, color: CREAM }}>
                            Buy {discount.min_qty}+, save ${Number(discount.per_item).toFixed(0)} per print
                          </p>
                          <p className="text-xs mt-0.5" style={{ ...fSans, color: GRAY }}>
                            {discount.min_qty} prints = ${(Number(art.price) * discount.min_qty - discount.per_item * discount.min_qty).toFixed(2)} (save ${(discount.per_item * discount.min_qty).toFixed(2)})
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Charity badge */}
                    {charityStats?.enabled && (
                      <div className="flex items-center gap-3 px-4 py-3 mb-4"
                        style={{ background: "rgba(74,222,128,0.07)", border: "1px solid rgba(74,222,128,0.25)" }}
                        data-testid="mc-charity-badge">
                        <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full text-[10px]"
                          style={{ background: "rgba(74,222,128,0.15)", color: "#4ade80" }}>♥</div>
                        <p className="text-xs" style={{ fontFamily: F.sans, color: "#4ade80" }}>
                          ${charityStats.donation_per_print.toFixed(0)} from every print goes to <strong>{charityStats.nonprofit_name}</strong>
                        </p>
                      </div>
                    )}

                    <button onClick={() => setShowCheckout(true)}
                      className="inline-flex items-center gap-3 px-8 py-4 text-xs tracking-[0.25em] uppercase font-bold transition-colors"
                      style={{ ...fSans, background: GOLD, color: BLACK }}
                      onMouseEnter={e => { e.currentTarget.style.background = "#E0BC72"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = GOLD; }}
                      data-testid="mc-buy-now-btn">
                      <ShoppingBag size={16} /> BUY NOW
                    </button>
                  </div>
                ) : null}

                {/* Tags */}
                {(art.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-8">
                    {art.tags.map(t => (
                      <span key={t} className="text-[9px] tracking-[0.15em] uppercase px-3 py-1"
                        style={{ ...fSans, border: `1px solid rgba(255,255,255,0.1)`, color: GRAY }}>{t}</span>
                    ))}
                  </div>
                )}

                {/* Handwritten quote */}
                <p className="mt-10 text-lg" style={{ ...fScript, color: `${GOLD}60` }}>
                  "Every mark has a reason." — Mick
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Mark by Mark Process ─── */}
        {(art.process_images || []).length > 0 && <MarkByMarkSection art={art} />}

        {/* ─── Charity Fundraiser Display ─── */}
        {charityStats?.enabled && <CharityFundraiserSection stats={charityStats} artTitle={art.title} />}

        {/* Shimmer divider */}
        <div className="h-[2px] mc-shimmer-gold" />

        {/* ─── Related Artwork + Browse More ─── */}
        <RelatedArtworkSection currentArt={art} />

        {/* ─── Checkout Modal ─── */}
        {showCheckout && canBuy && (
          <CheckoutModal art={art} maxQty={maxQty} discount={discount} charityStats={charityStats} onClose={() => setShowCheckout(false)} />
        )}
      </div>
    </Layout>
  );
}


/* ════════════════════════════════════════════════════════
   CHARITY FUNDRAISER SECTION
════════════════════════════════════════════════════════ */
function CharityFundraiserSection({ stats, artTitle }) {
  const [ref, vis] = useVisible(0.1);
  const [counted, setCounted] = useState(false);
  const [displayAmount, setDisplayAmount] = useState(0);

  // Animated counter
  useEffect(() => {
    if (!vis || counted) return;
    setCounted(true);
    const target = stats.total_raised;
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const iv = setInterval(() => {
      current += increment;
      if (current >= target) { setDisplayAmount(target); clearInterval(iv); }
      else setDisplayAmount(current);
    }, duration / steps);
    return () => clearInterval(iv);
  }, [vis, counted, stats.total_raised]);

  const pct = stats.goal_amount > 0 ? Math.min((stats.total_raised / stats.goal_amount) * 100, 100) : 0;

  return (
    <div ref={ref} className="mc-grit overflow-hidden relative" style={{ background: "#0a120a", borderTop: `1px solid rgba(74,222,128,0.12)`, borderBottom: `1px solid rgba(74,222,128,0.12)` }} data-testid="mc-charity-section">
      {/* Decorative glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full opacity-[0.06] pointer-events-none" style={{ background: "radial-gradient(circle, #4ade80 0%, transparent 70%)" }} />

      <div className="max-w-7xl mx-auto px-6 py-16 relative z-10">
        <div className={`transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {/* Header */}
          <div className="text-center mb-10">
            <span className="inline-block text-[10px] tracking-[0.4em] uppercase font-bold px-4 py-1.5 mb-4"
              style={{ fontFamily: F.sans, background: "rgba(74,222,128,0.12)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.25)" }}>
              ART FOR A CAUSE
            </span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-2"
              style={{ fontFamily: F.cond, color: C.text }}>
              THIS PIECE GIVES BACK.
            </h2>
            <MarkerStroke color="#4ade80" width="50px" height="4px" className="mx-auto mb-4" />
            <p className="text-sm max-w-xl mx-auto" style={{ fontFamily: F.sans, color: C.text2 }}>
              The original of <em style={{ color: "#4ade80" }}>"{artTitle}"</em> was donated to <strong style={{ color: C.text }}>{stats.nonprofit_name}</strong> and every print sold continues to raise money for their mission.
            </p>
          </div>

          {/* Big counter */}
          <div className="text-center mb-10">
            <div className="inline-block px-10 py-6" style={{ background: "rgba(74,222,128,0.06)", border: "2px solid rgba(74,222,128,0.2)" }}>
              <div className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ fontFamily: F.sans, color: "rgba(74,222,128,0.7)" }}>TOTAL RAISED</div>
              <div className="text-5xl sm:text-6xl font-black" style={{ fontFamily: F.cond, color: "#4ade80" }} data-testid="mc-charity-total">
                ${displayAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              {stats.goal_amount > 0 && (
                <div className="mt-4 w-full max-w-xs mx-auto">
                  <div className="h-2 w-full rounded-full" style={{ background: "rgba(74,222,128,0.1)" }}>
                    <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${vis ? pct : 0}%`, background: "#4ade80" }} data-testid="mc-charity-progress" />
                  </div>
                  <div className="flex justify-between text-[10px] mt-1.5" style={{ fontFamily: F.sans, color: "rgba(74,222,128,0.5)" }}>
                    <span>${stats.total_raised.toFixed(0)} raised</span>
                    <span>Goal: ${stats.goal_amount.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Breakdown + Nonprofit card */}
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Breakdown */}
            <div className="space-y-3" style={{ borderTop: "1px solid rgba(74,222,128,0.12)", paddingTop: "1rem" }}>
              <div className="text-[10px] tracking-[0.2em] uppercase mb-3" style={{ fontFamily: F.sans, color: "rgba(74,222,128,0.6)" }}>HOW IT BREAKS DOWN</div>
              {stats.original_sold_amount > 0 && (
                <div className="flex justify-between items-center py-2 px-3" style={{ background: "rgba(74,222,128,0.04)", border: "1px solid rgba(74,222,128,0.08)" }}>
                  <span className="text-xs" style={{ fontFamily: F.sans, color: C.text2 }}>Original raised at auction</span>
                  <span className="text-sm font-bold" style={{ fontFamily: F.cond, color: "#4ade80" }}>${stats.original_sold_amount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center py-2 px-3" style={{ background: "rgba(74,222,128,0.04)", border: "1px solid rgba(74,222,128,0.08)" }}>
                <span className="text-xs" style={{ fontFamily: F.sans, color: C.text2 }}>
                  Print sales ({stats.prints_sold} prints × ${stats.donation_per_print.toFixed(0)})
                </span>
                <span className="text-sm font-bold" style={{ fontFamily: F.cond, color: "#4ade80" }}>${stats.print_donations.toFixed(2)}</span>
              </div>
              {stats.manual_print_revenue > 0 && (
                <div className="flex justify-between items-center py-2 px-3" style={{ background: "rgba(74,222,128,0.04)", border: "1px solid rgba(74,222,128,0.08)" }}>
                  <span className="text-xs" style={{ fontFamily: F.sans, color: C.text2 }}>Additional fundraising</span>
                  <span className="text-sm font-bold" style={{ fontFamily: F.cond, color: "#4ade80" }}>${stats.manual_print_revenue.toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Nonprofit card */}
            <div className="p-5" style={{ background: "rgba(74,222,128,0.04)", border: "1px solid rgba(74,222,128,0.15)" }}>
              <div className="flex items-center gap-4 mb-3">
                {stats.nonprofit_logo && (
                  <img src={resolveImage(stats.nonprofit_logo)} alt={stats.nonprofit_name} className="w-14 h-14 object-contain rounded" data-testid="mc-charity-logo" />
                )}
                <div>
                  <h3 className="text-lg font-bold" style={{ fontFamily: F.cond, color: C.text }}>{stats.nonprofit_name}</h3>
                  {stats.nonprofit_url && (
                    <a href={stats.nonprofit_url.startsWith("http") ? stats.nonprofit_url : `https://${stats.nonprofit_url}`}
                      target="_blank" rel="noreferrer" className="text-xs hover:underline" style={{ fontFamily: F.sans, color: "#4ade80" }}>
                      Visit website ↗
                    </a>
                  )}
                </div>
              </div>
              {stats.nonprofit_description && (
                <p className="text-sm leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }}>{stats.nonprofit_description}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ════════════════════════════════════════════════════════
   MARK BY MARK SECTION
════════════════════════════════════════════════════════ */
function MarkByMarkSection({ art }) {
  const [ref, vis] = useVisible(0.1);
  return (
    <div ref={ref} className="mc-grit overflow-hidden" style={{ background: C.bg2, borderTop: `1px solid ${C.bSub}` }} data-testid="mc-mark-by-mark">
      <div className="max-w-7xl mx-auto px-6 py-16 relative z-10">
        <div className="flex items-end justify-between mb-10">
          <div className={`transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-1"
              style={{ fontFamily: F.brush, color: C.text }}>
              MARK BY MARK
            </h2>
            <MarkerStroke width="50px" className="mb-2" />
            <p className="text-sm tracking-wide" style={{ fontFamily: F.sans, color: C.text2 }}>
              The making of <em style={{ color: C.gold }}>{art.title}</em>
            </p>
          </div>
          <p className={`text-lg hidden md:block transition-all duration-700 delay-300 ${vis ? "opacity-100" : "opacity-0"}`}
            style={{ fontFamily: F.hand, color: `${C.gold}50` }}>
            Every line tells a story.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {art.process_images.map((step, i) => (
            <div key={i} className={`group transition-all duration-600 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              style={{ transitionDelay: `${200 + i * 100}ms` }}>
              <div className="aspect-square overflow-hidden mb-2 relative"
                style={{ background: C.bg, border: `1px solid ${C.bSub}` }}>
                <img src={resolveImage(step.image)} alt={step.caption || `Step ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute top-2 left-2 text-[9px] tracking-[0.2em] px-2 py-0.5"
                  style={{ fontFamily: F.sans, background: C.gold, color: C.bg, fontWeight: 700 }}>
                  {i + 1}
                </div>
              </div>
              {step.caption && (
                <p className="text-xs leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }}>{step.caption}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   RELATED ARTWORK + BROWSE MORE
════════════════════════════════════════════════════════ */
function RelatedArtworkSection({ currentArt }) {
  const [ref, vis] = useVisible(0.05);
  const [related, setRelated] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!currentArt?.id) return;
    http.get("/marker-collection/public/products").then(({ data }) => {
      const pool = (data || []).filter(a =>
        a.id !== currentArt.id && !a.linked_parent_id && a.status === "active"
      );
      const currentTags = new Set(currentArt.tags || []);
      const scored = pool.map(a => {
        let score = 0;
        (a.tags || []).forEach(t => { if (currentTags.has(t)) score += 3; });
        if (a.art_type === currentArt.art_type) score += 1;
        if (a.charity?.enabled && currentArt.charity?.enabled) score += 1;
        score += Math.random() * 0.5;
        return { ...a, _score: score };
      });
      scored.sort((a, b) => b._score - a._score);
      setRelated(scored.slice(0, 4));
      // Small delay to let DOM render before animating in
      setTimeout(() => setReady(true), 100);
    }).catch(() => {});
  }, [currentArt]);

  // Show content when visible OR when data is ready (handles bottom-of-page IO issues)
  const show = vis || ready;

  if (related.length === 0) {
    // Fall back to simple browse-more CTA if no related artwork
    return (
      <div ref={ref} className="mc-grit overflow-hidden" style={{ background: C.bg, borderTop: `1px solid ${C.bSub}` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 flex flex-col sm:flex-row items-center justify-between gap-6 relative z-10">
          <div className={`transition-all duration-700 ${show ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <p className="text-2xl font-black" style={{ fontFamily: F.brush, color: C.text }}>SEE MORE.</p>
            <p className="text-xs tracking-[0.2em] uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>Browse the full collection</p>
          </div>
          <Link to="/marker-collection"
            className={`inline-flex items-center gap-3 px-7 py-3.5 text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300 hover:gap-5 ${show ? "opacity-100" : "opacity-0"}`}
            style={{ fontFamily: F.sans, background: C.gold, color: C.bg }}
            data-testid="mc-detail-browse-more">
            BACK TO COLLECTION <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <section ref={ref} className="mc-grit overflow-hidden" style={{ background: C.bg2, borderTop: `1px solid ${C.bSub}` }} data-testid="mc-related-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16 relative z-10">
        {/* Header */}
        <div className={`mb-8 transition-all duration-700 ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase font-bold mb-2" style={{ fontFamily: F.sans, color: C.red }}>
            YOU MIGHT ALSO LIKE
          </p>
          <p className="text-2xl sm:text-3xl font-black" style={{ fontFamily: F.brush, color: C.text }}>
            More From The Collection
          </p>
        </div>

        {/* Grid — 2 col mobile, 4 col desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="mc-related-grid">
          {related.map((art, i) => {
            const isSold = art.original_status === "sold" || (art.quantity != null && art.quantity <= 0);
            return (
              <Link key={art.id} to={`/marker-collection/${art.slug}`}
                className="group block relative"
                style={{
                  opacity: show ? 1 : 0, transform: show ? "translateY(0)" : "translateY(24px)",
                  transition: `all 0.5s cubic-bezier(0.25,0.46,0.45,0.94) ${200 + i * 100}ms`,
                }}
                data-testid={`mc-related-card-${art.slug}`}>
                <div className="relative overflow-hidden" style={{ border: `1px solid ${C.bSub}` }}>
                  {art.main_image ? (
                    <img src={resolveImage(art.main_image)} alt={art.title}
                      className="w-full aspect-[3/4] object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  ) : (
                    <div className="w-full aspect-[3/4] flex items-center justify-center" style={{ background: C.card }}>
                      <Palette size={28} style={{ color: `${C.gold}20` }} />
                    </div>
                  )}
                  {/* Type badge */}
                  <div className="absolute top-2 left-2 px-2 py-0.5 text-[7px] tracking-[0.25em] uppercase font-bold"
                    style={{ fontFamily: F.sans, background: art.art_type === "Original" ? C.red : C.gold, color: art.art_type === "Original" ? "#fff" : C.bg }}>
                    {art.art_type || "ART"}
                  </div>
                  {/* Sale badge */}
                  {art.on_sale && art.sale_price > 0 && art.sale_price < art.price && (
                    <div className="absolute top-2 right-2 px-2 py-0.5 text-[7px] tracking-[0.15em] uppercase font-bold"
                      style={{ background: "#FF3B30", color: "#fff", fontFamily: F.sans }}>SALE</div>
                  )}
                  {/* Mobile price overlay */}
                  <div className="sm:hidden absolute bottom-2 right-2 px-2 py-1"
                    style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)" }}>
                    {art.on_sale && art.sale_price > 0 && art.sale_price < art.price ? (
                      <span className="flex items-baseline gap-1">
                        <span className="text-sm font-black" style={{ fontFamily: F.cond, color: "#FF3B30" }}>
                          ${Number(art.sale_price).toFixed(0)}
                        </span>
                        <span className="text-[9px] font-bold line-through" style={{ fontFamily: F.cond, color: "#999" }}>
                          ${Number(art.price || 0).toFixed(0)}
                        </span>
                      </span>
                    ) : (
                      <span className="text-sm font-black" style={{ fontFamily: F.cond, color: isSold ? C.text2 : C.gold }}>
                        ${Number(art.price || 0).toFixed(0)}
                      </span>
                    )}
                  </div>
                  {/* Sold overlay */}
                  {isSold && (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.65)" }}>
                      <span className="text-xs tracking-[0.3em] uppercase font-black" style={{ fontFamily: F.cond, color: C.text }}>SOLD</span>
                    </div>
                  )}
                </div>
                <div className="mt-1.5 sm:mt-2 flex items-end justify-between gap-1">
                  <p className="text-[11px] sm:text-sm font-bold truncate" style={{ fontFamily: F.sans, color: C.text }}>{art.title}</p>
                  <span className="hidden sm:block text-base font-black flex-shrink-0" style={{ fontFamily: F.cond, color: isSold ? C.text2 : (art.on_sale && art.sale_price > 0 && art.sale_price < art.price) ? "#FF3B30" : C.gold }}>
                    {art.on_sale && art.sale_price > 0 && art.sale_price < art.price ? (
                      <><span>${Number(art.sale_price).toFixed(0)}</span> <span className="text-xs line-through text-zinc-500">${Number(art.price).toFixed(0)}</span></>
                    ) : (
                      <>${Number(art.price || 0).toFixed(0)}</>
                    )}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Browse More CTA */}
        <div className={`text-center mt-10 transition-all duration-700 delay-500 ${show ? "opacity-100" : "opacity-0"}`}>
          <Link to="/marker-collection"
            className="inline-flex items-center gap-3 px-7 py-3.5 text-[10px] sm:text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300 hover:gap-5 group"
            style={{ fontFamily: F.sans, border: `2px solid ${C.bGold}`, color: C.gold }}
            data-testid="mc-detail-browse-more">
            VIEW ALL ARTWORK <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}


/* ════════════════════════════════════════════════════════
   SOCIAL SHARE CARD — Post-checkout charity share
════════════════════════════════════════════════════════ */
function ShareForACauseCard({ artTitle, nonprofitName, artSlug }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/marker-collection/${artSlug}`;
  const shareText = `I just supported ${nonprofitName} by buying "${artTitle}" from Simple Truths Club. Every print raises money for a great cause.`;

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;

  const copyLink = () => {
    navigator.clipboard.writeText(`${shareText}\n${shareUrl}`).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="mx-6 mb-4 p-5 text-center" style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.2)" }} data-testid="mc-share-card">
      <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full" style={{ background: "rgba(74,222,128,0.12)" }}>
        <Heart size={18} fill="#4ade80" color="#4ade80" />
      </div>
      <p className="text-sm font-bold mb-1" style={{ fontFamily: F.cond, color: C.text }}>
        YOU'RE MAKING A DIFFERENCE
      </p>
      <p className="text-xs mb-4" style={{ fontFamily: F.sans, color: C.text2 }}>
        Your purchase supports <strong style={{ color: "#4ade80" }}>{nonprofitName}</strong>. Spread the word!
      </p>
      <div className="flex justify-center gap-2">
        <a href={twitterUrl} target="_blank" rel="noreferrer"
          className="px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase font-bold transition-colors"
          style={{ fontFamily: F.sans, background: "#1DA1F2", color: "#fff" }}
          data-testid="mc-share-twitter">
          Share on X
        </a>
        <a href={fbUrl} target="_blank" rel="noreferrer"
          className="px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase font-bold transition-colors"
          style={{ fontFamily: F.sans, background: "#1877F2", color: "#fff" }}
          data-testid="mc-share-facebook">
          Facebook
        </a>
        <button onClick={copyLink}
          className="px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase font-bold transition-colors"
          style={{ fontFamily: F.sans, background: copied ? "#4ade80" : "rgba(255,255,255,0.1)", color: copied ? C.bg : C.text, border: "1px solid rgba(255,255,255,0.1)" }}
          data-testid="mc-share-copy">
          {copied ? "Copied!" : "Copy Link"}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   CHECKOUT MODAL — Dark/Gold treatment
═══════════════════════════════════════════════════ */
function CheckoutModal({ art, maxQty, discount, charityStats, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [qty, setQty] = useState(1);
  const [address, setAddress] = useState({ line1: "", line2: "", city: "", state: "", zip: "" });
  const [payMethod, setPayMethod] = useState("stripe");
  const [deliveryMethod, setDeliveryMethod] = useState("ship");
  const [processing, setProcessing] = useState(false);
  const [manualResult, setManualResult] = useState(null);
  const [copied, setCopied] = useState("");
  const isOriginal = art.art_type === "Original";

  const shippingInfo = discount?.shipping || { flat_rate: 6.95, free_over: 75, pickup_enabled: true, pickup_info: "" };

  const effectivePrice = (art.on_sale && art.sale_price > 0 && art.sale_price < art.price) ? art.sale_price : (art.price || 0);
  const discountActive = discount?.enabled && !isOriginal && qty >= (discount?.min_qty || 2);
  const discountPerItem = discountActive ? (discount?.per_item || 0) : 0;
  const subtotal = effectivePrice * qty;
  const totalDiscount = discountPerItem * qty;
  const lineTotal = subtotal - totalDiscount;

  // Shipping calculation
  const shippingCost = deliveryMethod === "ship"
    ? (lineTotal >= shippingInfo.free_over ? 0 : shippingInfo.flat_rate)
    : 0;
  const orderTotal = lineTotal + shippingCost;

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(label);
    setTimeout(() => setCopied(""), 2000);
  };

  const validateForm = () => {
    if (!name.trim() || !email.trim()) { toast.error("Name and email are required"); return false; }
    if (deliveryMethod === "ship") {
      if (!address.line1.trim() || !address.city.trim() || !address.state.trim() || !address.zip.trim()) {
        toast.error("Please fill in the shipping address"); return false;
      }
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setProcessing(true);
    const payload = { artwork_id: art.id, quantity: qty, name, email, delivery_method: deliveryMethod, shipping_address: deliveryMethod === "ship" ? address : null };
    try {
      if (payMethod === "stripe") {
        const { data } = await http.post("/marker-collection/public/checkout", payload);
        if (data.url) window.location.href = data.url;
      } else {
        const { data } = await http.post("/marker-collection/public/checkout/manual", { ...payload, payment_method: payMethod });
        setManualResult(data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
    } finally {
      setProcessing(false);
    }
  };

  /* ─── Manual payment confirmation ─── */
  if (manualResult) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="mc-manual-confirm">
        <div className="w-full max-w-md shadow-2xl text-center" style={{ background: DGRAY }}>
          <div className="px-6 pt-8 pb-2">
            <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center" style={{ background: GOLD }}>
              {payMethod === "venmo" ? <Smartphone size={22} style={{ color: BLACK }} /> : <DollarSign size={22} style={{ color: BLACK }} />}
            </div>
            <h3 className="text-2xl font-black mb-1" style={{ ...fCond, color: CREAM }}>Almost yours.</h3>
            <p className="text-sm" style={{ ...fSans, color: GRAY }}>Complete your payment on {manualResult.label}</p>
          </div>
          <div className="px-6 py-6 space-y-4">
            <div className="p-5" style={{ background: BLACK, border: `1px solid rgba(255,255,255,0.08)` }}>
              <p className="text-[10px] tracking-[0.2em] uppercase mb-2" style={{ ...fSans, color: `${GOLD}80` }}>Send this amount</p>
              <p className="text-3xl font-black" style={{ ...fCond, color: CREAM }}>${manualResult.total.toFixed(2)}</p>
            </div>
            <div className="p-5" style={{ background: BLACK, border: `1px solid rgba(255,255,255,0.08)` }}>
              <p className="text-[10px] tracking-[0.2em] uppercase mb-2" style={{ ...fSans, color: `${GOLD}80` }}>
                To this {payMethod === "venmo" ? "username" : "tag"}
              </p>
              <button onClick={() => copyText(manualResult.handle, "handle")}
                className="text-xl font-bold flex items-center gap-2 mx-auto hover:opacity-80 transition-opacity"
                style={{ ...fSans, color: CREAM }} data-testid="mc-manual-copy-handle">
                {manualResult.handle}
                {copied === "handle" ? <Check size={16} style={{ color: "#4ade80" }} /> : <Copy size={14} style={{ color: GRAY }} />}
              </button>
            </div>
            <div className="p-5" style={{ background: BLACK, border: `1px solid rgba(255,255,255,0.08)` }}>
              <p className="text-[10px] tracking-[0.2em] uppercase mb-2" style={{ ...fSans, color: `${GOLD}80` }}>Include in the note</p>
              <button onClick={() => copyText(art.title, "note")}
                className="text-sm font-medium flex items-center gap-2 mx-auto hover:opacity-80 transition-opacity"
                style={{ ...fSans, color: CREAM }} data-testid="mc-manual-copy-note">
                "{art.title}"
                {copied === "note" ? <Check size={16} style={{ color: "#4ade80" }} /> : <Copy size={14} style={{ color: GRAY }} />}
              </button>
            </div>
            <p className="text-xs leading-relaxed" style={{ ...fSans, color: `${GRAY}90` }}>
              Mickey will verify your payment within 24 hours. Confirmation email will be sent to <strong style={{ color: CREAM }}>{email}</strong>.
            </p>
            {payMethod === "venmo" && (
              <a href={`https://venmo.com/u/MickeyTrivett?txn=pay&amount=${manualResult.total.toFixed(2)}&note=${encodeURIComponent(art.title)}`}
                target="_blank" rel="noopener noreferrer"
                className="block w-full py-3.5 text-xs tracking-[0.2em] uppercase font-bold text-center transition-colors"
                style={{ ...fSans, background: "#008CFF", color: "#fff" }} data-testid="mc-manual-venmo-link">
                Open Venmo
              </a>
            )}
            {payMethod === "cashapp" && (
              <a href={`https://cash.app/$MickeyTrivett/${manualResult.total.toFixed(2)}`}
                target="_blank" rel="noopener noreferrer"
                className="block w-full py-3.5 text-xs tracking-[0.2em] uppercase font-bold text-center transition-colors"
                style={{ ...fSans, background: "#00D54B", color: "#fff" }} data-testid="mc-manual-cashapp-link">
                Open Cash App
              </a>
            )}
          </div>
          {/* Social Share Card for charity artworks */}
          {charityStats?.enabled && (
            <ShareForACauseCard artTitle={art.title} nonprofitName={charityStats.nonprofit_name} artSlug={art.slug} />
          )}
          <div className="px-6 pb-6">
            <button onClick={onClose} className="text-xs underline transition-opacity hover:opacity-80"
              style={{ ...fSans, color: GRAY }} data-testid="mc-manual-done">Done</button>
          </div>
        </div>
      </div>
    );
  }

  /* ─── Checkout form ─── */
  const inputCls = "w-full px-4 py-2.5 text-sm focus:outline-none";
  const inputStyle = { ...fSans, background: "rgba(255,255,255,0.04)", border: `1px solid rgba(255,255,255,0.1)`, color: CREAM };
  const labelCls = "text-[10px] tracking-[0.2em] uppercase block mb-1.5";
  const labelStyle = { ...fSans, color: `${GOLD}90` };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="mc-checkout-modal">
      <div className="w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto" style={{ background: DGRAY }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ background: BLACK, borderBottom: `1px solid rgba(255,255,255,0.08)` }}>
          <div>
            <h3 className="text-lg font-black" style={{ ...fCond, color: CREAM }}>Purchase Artwork</h3>
            <p className="text-[10px] tracking-[0.15em] uppercase" style={{ ...fSans, color: GOLD }}>Secure Checkout</p>
          </div>
          <button onClick={onClose} className="hover:opacity-70 transition-opacity" style={{ color: GRAY }} data-testid="mc-checkout-close">
            <X size={18} />
          </button>
        </div>

        {/* Summary */}
        <div className="px-6 py-4" style={{ background: BLACK, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
          <div className="flex items-center gap-4">
            {art.main_image && (
              <img src={resolveImage(art.main_image)} alt="" className="w-16 h-16 object-cover"
                style={{ border: `1px solid rgba(255,255,255,0.06)` }} />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ ...fSans, color: CREAM }}>{art.title}</p>
              <p className="text-xs" style={{ ...fSans, color: GRAY }}>{art.art_type}</p>
            </div>
            <p className="text-lg font-black" style={{ ...fCond, color: CREAM }}>${Number(art.price).toFixed(2)}</p>
          </div>
          {discount?.enabled && !isOriginal && !discountActive && qty === 1 && (
            <div className="mt-3 px-3 py-2 text-center" style={{ background: `${GOLD}10`, border: `1px solid ${GOLD}25` }} data-testid="mc-checkout-discount-hint">
              <p className="text-xs" style={{ ...fSans, color: GOLD }}>
                Add {(discount.min_qty || 2) - qty} more to save <strong>${Number(discount.per_item).toFixed(0)}/print</strong>
              </p>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {/* Quantity */}
          {!isOriginal && (
            <div>
              <label style={labelStyle} className={labelCls}>Quantity</label>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setQty(q => Math.max(1, q - 1))}
                  className="w-9 h-9 flex items-center justify-center text-lg transition-colors"
                  style={{ border: `1px solid rgba(255,255,255,0.1)`, color: CREAM, background: "rgba(255,255,255,0.04)" }}
                  data-testid="mc-checkout-qty-minus">-</button>
                <input type="number" min="1" max={maxQty} value={qty}
                  onChange={e => setQty(Math.min(maxQty, Math.max(1, parseInt(e.target.value) || 1)))}
                  className="w-16 text-center py-2 text-sm focus:outline-none"
                  style={{ ...fSans, background: "rgba(255,255,255,0.04)", border: `1px solid rgba(255,255,255,0.1)`, color: CREAM }}
                  data-testid="mc-checkout-qty" />
                <button type="button" onClick={() => setQty(q => Math.min(maxQty, q + 1))}
                  className="w-9 h-9 flex items-center justify-center text-lg transition-colors"
                  style={{ border: `1px solid rgba(255,255,255,0.1)`, color: CREAM, background: "rgba(255,255,255,0.04)" }}
                  data-testid="mc-checkout-qty-plus">+</button>
                {art.quantity != null && (
                  <span className="text-xs" style={{ ...fSans, color: GRAY }}>{art.quantity} available</span>
                )}
              </div>
              {discountActive && (
                <div className="mt-3 px-3 py-2 flex items-center justify-between"
                  style={{ background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.2)" }}
                  data-testid="mc-checkout-discount-active">
                  <span className="text-xs font-medium" style={{ ...fSans, color: "#4ade80" }}>Multi-print discount applied</span>
                  <span className="text-xs font-bold" style={{ ...fSans, color: "#4ade80" }}>-${totalDiscount.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          <div>
            <label style={labelStyle} className={labelCls}>Full Name *</label>
            <input className={inputCls} style={inputStyle} value={name} onChange={e => setName(e.target.value)} required data-testid="mc-checkout-name" />
          </div>
          <div>
            <label style={labelStyle} className={labelCls}>Email *</label>
            <input type="email" className={inputCls} style={inputStyle} value={email} onChange={e => setEmail(e.target.value)} required data-testid="mc-checkout-email" />
          </div>

          {/* Delivery Method */}
          <div>
            <p style={labelStyle} className={labelCls + " mt-2"}>Delivery Method</p>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setDeliveryMethod("ship")}
                className="flex items-center gap-3 px-4 py-3 text-left transition-all"
                style={{
                  border: `2px solid ${deliveryMethod === "ship" ? GOLD : "rgba(255,255,255,0.08)"}`,
                  background: deliveryMethod === "ship" ? `${GOLD}08` : "transparent",
                }}
                data-testid="mc-delivery-ship">
                <Truck size={16} style={{ color: deliveryMethod === "ship" ? GOLD : GRAY }} />
                <div>
                  <p className="text-sm font-medium" style={{ ...fSans, color: CREAM }}>Ship It</p>
                  <p className="text-[10px]" style={{ ...fSans, color: GRAY }}>
                    {shippingInfo.flat_rate > 0 ? `$${shippingInfo.flat_rate.toFixed(2)} flat rate` : "Free"} 
                  </p>
                </div>
                {deliveryMethod === "ship" && <Check size={14} className="ml-auto" style={{ color: GOLD }} />}
              </button>
              {shippingInfo.pickup_enabled && (
                <button type="button" onClick={() => setDeliveryMethod("pickup")}
                  className="flex items-center gap-3 px-4 py-3 text-left transition-all"
                  style={{
                    border: `2px solid ${deliveryMethod === "pickup" ? GOLD : "rgba(255,255,255,0.08)"}`,
                    background: deliveryMethod === "pickup" ? `${GOLD}08` : "transparent",
                  }}
                  data-testid="mc-delivery-pickup">
                  <MapPin size={16} style={{ color: deliveryMethod === "pickup" ? GOLD : GRAY }} />
                  <div>
                    <p className="text-sm font-medium" style={{ ...fSans, color: CREAM }}>Pickup</p>
                    <p className="text-[10px]" style={{ ...fSans, color: GRAY }}>Free — meet up</p>
                  </div>
                  {deliveryMethod === "pickup" && <Check size={14} className="ml-auto" style={{ color: GOLD }} />}
                </button>
              )}
            </div>
            {deliveryMethod === "ship" && lineTotal >= shippingInfo.free_over && (
              <p className="mt-2 text-[10px] tracking-[0.12em] uppercase text-center" style={{ ...fSans, color: "#4ade80" }}
                data-testid="mc-free-shipping-note">
                Free shipping on orders ${shippingInfo.free_over.toFixed(0)}+
              </p>
            )}
          </div>

          {/* Shipping Address — only for ship */}
          {deliveryMethod === "ship" && (
            <div data-testid="mc-checkout-address-section">
              <p style={labelStyle} className={labelCls + " mt-2"}>Shipping Address</p>
              <input className={inputCls} style={inputStyle} placeholder="Street Address *" value={address.line1} onChange={e => setAddress(p => ({ ...p, line1: e.target.value }))} required data-testid="mc-checkout-line1" />
              <input className={inputCls + " mt-3"} style={inputStyle} placeholder="Apt / Suite (optional)" value={address.line2} onChange={e => setAddress(p => ({ ...p, line2: e.target.value }))} data-testid="mc-checkout-line2" />
              <div className="grid grid-cols-3 gap-3 mt-3">
                <input className={inputCls} style={inputStyle} placeholder="City *" value={address.city} onChange={e => setAddress(p => ({ ...p, city: e.target.value }))} required data-testid="mc-checkout-city" />
                <input className={inputCls} style={inputStyle} placeholder="State *" value={address.state} onChange={e => setAddress(p => ({ ...p, state: e.target.value }))} required data-testid="mc-checkout-state" />
                <input className={inputCls} style={inputStyle} placeholder="ZIP *" value={address.zip} onChange={e => setAddress(p => ({ ...p, zip: e.target.value }))} required data-testid="mc-checkout-zip" />
              </div>
            </div>
          )}

          {/* Pickup info */}
          {deliveryMethod === "pickup" && (
            <div className="px-4 py-3" style={{ background: `${GOLD}06`, border: `1px solid ${GOLD}20` }} data-testid="mc-pickup-info">
              <p className="text-[10px] tracking-[0.2em] uppercase mb-1" style={{ ...fSans, color: GOLD }}>Pickup / Meet-up</p>
              <p className="text-xs leading-relaxed" style={{ ...fSans, color: GRAY }}>
                {shippingInfo.pickup_info || "East Tennessee — exact meetup spot arranged over email after purchase."}
              </p>
            </div>
          )}

          {/* Payment method */}
          <div>
            <p style={labelStyle} className={labelCls + " mt-2"}>Payment Method</p>
            <div className="space-y-2">
              {[
                { key: "stripe", label: "Credit / Debit Card", desc: "Secure checkout via Stripe", icon: <CreditCard size={18} /> },
                { key: "cashapp", label: "Cash App", desc: "Send to $MickeyTrivett", icon: <DollarSign size={18} /> },
                { key: "venmo", label: "Venmo", desc: "Send to @MickeyTrivett", icon: <Smartphone size={18} /> },
              ].map(m => (
                <button key={m.key} type="button" onClick={() => setPayMethod(m.key)}
                  className="w-full flex items-center gap-4 px-4 py-3 text-left transition-all"
                  style={{
                    border: `2px solid ${payMethod === m.key ? GOLD : "rgba(255,255,255,0.08)"}`,
                    background: payMethod === m.key ? `${GOLD}08` : "transparent",
                  }}
                  data-testid={`mc-pay-${m.key}`}>
                  <div style={{ color: payMethod === m.key ? GOLD : GRAY }}>{m.icon}</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium" style={{ ...fSans, color: CREAM }}>{m.label}</p>
                    <p className="text-[11px]" style={{ ...fSans, color: GRAY }}>{m.desc}</p>
                  </div>
                  {payMethod === m.key && <Check size={16} style={{ color: GOLD }} />}
                </button>
              ))}
            </div>
          </div>

          {/* Price breakdown — always show to display shipping */}
          <div className="px-4 py-3 space-y-1.5" style={{ border: `1px solid rgba(255,255,255,0.08)` }} data-testid="mc-checkout-breakdown">
            <div className="flex justify-between text-xs" style={{ ...fSans, color: GRAY }}>
              <span>{qty} x ${Number(art.price).toFixed(2)}</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            {discountActive && (
              <div className="flex justify-between text-xs font-medium" style={{ ...fSans, color: "#4ade80" }}>
                <span>Multi-print discount ({qty} x -${discountPerItem.toFixed(2)})</span>
                <span>-${totalDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-xs" style={{ ...fSans, color: GRAY }}>
              <span>{deliveryMethod === "pickup" ? "Pickup (free)" : "Shipping"}</span>
              <span style={shippingCost === 0 ? { color: "#4ade80" } : {}}>
                {shippingCost === 0 ? "FREE" : `$${shippingCost.toFixed(2)}`}
              </span>
            </div>
            <div className="flex justify-between text-sm font-bold pt-1.5" style={{ ...fSans, color: CREAM, borderTop: `1px solid rgba(255,255,255,0.08)` }}>
              <span>Total</span>
              <span>${orderTotal.toFixed(2)}</span>
            </div>
          </div>

          <button type="submit" disabled={processing}
            className="w-full py-4 text-xs tracking-[0.25em] uppercase font-bold flex items-center justify-center gap-2 disabled:opacity-50 mt-4 transition-colors"
            style={{ ...fSans, background: GOLD, color: BLACK }}
            data-testid="mc-checkout-submit">
            {processing ? <Loader2 size={16} className="animate-spin" /> : <ShoppingBag size={16} />}
            {processing ? "Processing..."
              : payMethod === "stripe" ? `PAY $${orderTotal.toFixed(2)}${qty > 1 ? ` (${qty} items)` : ""}`
              : `PLACE ORDER — $${orderTotal.toFixed(2)}`}
          </button>
        </form>
      </div>
    </div>
  );
}
